import { httpClient } from '@/utils/httpClient';
import { entityService } from '@/services/EntityService';
import { documentService } from '@/services/documents';
import { iemService } from '@/services/iem';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'LossRecordService' });

export type TransactionType = 'DD200' | 'DA7923';
export type WorkflowStatus = 'suspense' | 'complete' | 'corrected';

export interface LossRecord {
  id: string;
  entity_identifier: string | null;
  display_name: string | null;
  entity_data: LossRecordData;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface AssetSnapshot {
  id: string;
  display_name: string | null;
  entity_identifier: string | null;
  nsn: string | null;
  lin: string | null;
  serial_number: string | null;
}

export interface LossRecordData {
  transaction_type: TransactionType;
  workflow_status: WorkflowStatus;
  person_id: string;
  asset_ids: string[];
  asset_snapshots?: AssetSnapshot[];
  comments?: string | null;
  include_contamination_memo?: boolean;
  suspense_at: string;
  completed_at?: string | null;
  generated_document_ids?: string[];
  uploaded_document_ids?: string[];
}

export interface LossRecordViewRow {
  id: string;
  entity_identifier: string | null;
  display_name: string | null;
  entity_data: LossRecordData;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  transaction_type: TransactionType;
  workflow_status: WorkflowStatus;
  person_id: string;
  comments: string | null;
  suspense_at: string | null;
  completed_at: string | null;
  include_contamination_memo: boolean | null;
  asset_count: number;
  generated_doc_count: number;
  uploaded_doc_count: number;
  person_display_name: string | null;
  person_identifier: string | null;
  person_email: string | null;
}

export interface CreateLossRecordInput {
  transactionType: TransactionType;
  personId: string;
  assetIds: string[];
  comments?: string;
  includeContaminationMemo?: boolean;
}

export interface CompleteLossRecordInput {
  id: string;
  uploadedDocumentIds: string[];
  performedById: string;
}

interface HandReceiptEntity {
  id: string;
  entity_data: {
    person_id?: string;
    asset_id?: string;
    cancelled_at?: string | null;
  };
}

class LossRecordService {
  /**
   * Get active hand receipts for the given asset IDs and person
   * Used to close hand receipts when processing FLIPL
   */
  private async getHandReceiptsForAssets(
    personId: string,
    assetIds: string[],
  ): Promise<Map<string, string>> {
    // Map of assetId -> handReceiptId
    const result = new Map<string, string>();

    // Query hand receipts where asset_id is in our list
    // PostgREST doesn't support IN for jsonb fields easily, so we query all active
    // hand receipts for this person and filter client-side
    const params = new URLSearchParams({
      entity_type: 'eq.hand_receipt',
      is_current: 'eq.true',
      status: 'eq.active',
      select: 'id,entity_data',
    });

    const handReceipts = await httpClient.get<HandReceiptEntity[]>(
      `/api/rest/data/v1/entities?${params.toString()}`,
    );

    for (const hr of handReceipts) {
      const hrPersonId = hr.entity_data?.person_id;
      const hrAssetId = hr.entity_data?.asset_id;

      // status filter already ensures these are active (not closed)
      if (hrPersonId === personId && hrAssetId && assetIds.includes(hrAssetId)) {
        result.set(hrAssetId, hr.id);
      }
    }

    return result;
  }

  /**
   * Remove assets from hand receipts by calling the turn-in endpoint
   * with disposition 'cleared' (marks assets as disposed).
   * When sourceDocumentNumber is provided, the turn-in reuses that number
   * instead of reserving a new one (AB#29441).
   * When lossRecordId is provided, the NR transaction's metadata stores it
   * so document generation can later propagate the document number (AB#29564).
   */
  private async removeAssetsFromHandReceipts(
    personId: string,
    assetIds: string[],
    sourceDocumentNumber?: string,
    lossRecordId?: string,
  ): Promise<void> {
    const handReceiptMap = await this.getHandReceiptsForAssets(personId, assetIds);

    if (handReceiptMap.size === 0) {
      log.warn('No active hand receipts found for the specified assets');
      return;
    }

    const items = assetIds
      .filter((assetId) => handReceiptMap.has(assetId))
      .map((assetId) => ({
        handReceiptId: handReceiptMap.get(assetId)!,
        assetId,
        disposition: 'cleared' as const,
      }));

    if (items.length === 0) {
      return;
    }

    await iemService.completeTurnIn({
      memberId: personId,
      items,
      ...(sourceDocumentNumber ? { sourceDocumentNumber } : {}),
      ...(lossRecordId ? { lossRecordId } : {}),
    });
  }

  /**
   * List all loss records using the view
   */
  async list(): Promise<LossRecordViewRow[]> {
    return httpClient.get<LossRecordViewRow[]>(
      '/api/rest/data/v1/loss_record_view?order=created_at.desc',
    );
  }

  /**
   * Get a single loss record by ID
   */
  async get(id: string): Promise<LossRecord | null> {
    const entity = await entityService.get({ type: 'loss_record', id });
    if (!entity) return null;
    return {
      id: entity.id,
      entity_identifier: entity.entity_identifier ?? null,
      display_name: entity.display_name,
      entity_data: entity.entity_data as unknown as LossRecordData,
      status: entity.status,
      created_at: entity.created_at,
      updated_at: entity.updated_at,
      created_by: entity.created_by ?? null,
    };
  }

  /**
   * Fetch asset details for creating snapshots
   */
  private async fetchAssetSnapshots(assetIds: string[]): Promise<AssetSnapshot[]> {
    if (assetIds.length === 0) return [];

    const params = new URLSearchParams({
      entity_type: 'eq.asset',
      is_current: 'eq.true',
      id: `in.(${assetIds.join(',')})`,
      select: 'id,display_name,entity_identifier,entity_data',
    });

    interface AssetEntity {
      id: string;
      display_name: string | null;
      entity_identifier: string | null;
      entity_data: {
        nsn?: string | null;
        lin?: string | null;
        serial_number?: string | null;
      } | null;
    }

    const assets = await httpClient.get<AssetEntity[]>(`/api/rest/data/v1/entities?${params}`);

    return assets.map((asset) => ({
      id: asset.id,
      display_name: asset.display_name,
      entity_identifier: asset.entity_identifier,
      nsn: asset.entity_data?.nsn ?? null,
      lin: asset.entity_data?.lin ?? null,
      serial_number: asset.entity_data?.serial_number ?? null,
    }));
  }

  /**
   * Create a new loss record
   * For DD200: also removes assets from hand receipt
   * For DA7923: assets remain on hand receipt until complete
   */
  async create(input: CreateLossRecordInput): Promise<LossRecord> {
    // Fetch asset snapshots BEFORE any modifications
    // This captures the asset state at the time of loss record creation
    const assetSnapshots = await this.fetchAssetSnapshots(input.assetIds);

    const now = new Date().toISOString();
    const entityData: LossRecordData = {
      transaction_type: input.transactionType,
      workflow_status: 'suspense',
      person_id: input.personId,
      asset_ids: input.assetIds,
      asset_snapshots: assetSnapshots,
      comments: input.comments ?? null,
      include_contamination_memo: input.includeContaminationMemo ?? false,
      suspense_at: now,
      completed_at: null,
      generated_document_ids: [],
      uploaded_document_ids: [],
    };

    const entity = await entityService.create({
      entity_type: 'loss_record',
      display_name: `${input.transactionType} - suspense`,
      entity_data: entityData as unknown as Record<string, unknown>,
      status: 'active',
    });

    // Queue a document-generation payload so the workflow has a persisted record
    // of the required document even before the user fills the form fields.
    try {
      await documentService.saveDocumentPayload({
        documentType: entity.entity_data?.transaction_type as string,
        payload: { relatedEntityId: entity.id },
      });
    } catch (docPayloadError) {
      log.warn('Failed to queue document generation payload for loss record', {
        id: entity.id,
        transactionType: input.transactionType,
        error: String(docPayloadError),
      });
    }

    // For DD200, remove assets from hand receipt on create
    // For DA7923, assets are removed on Complete
    if (input.transactionType === 'DD200') {
      await this.removeAssetsFromHandReceipts(input.personId, input.assetIds, undefined, entity.id);
    }

    const lossRecord: LossRecord = {
      id: entity.id,
      entity_identifier: entity.entity_identifier ?? null,
      display_name: entity.display_name,
      entity_data: entity.entity_data as unknown as LossRecordData,
      status: entity.status,
      created_at: entity.created_at,
      updated_at: entity.updated_at,
      created_by: entity.created_by ?? null,
    };

    // Create a transaction entity so the loss record appears in the Transaction
    // Log (and the Item-Level Correction search) while still in suspense.
    try {
      await this.createTransactionLogEntry(lossRecord, now, entity.created_by ?? '');
    } catch (txnError) {
      log.warn('Failed to create transaction log entry for new loss record', {
        id: entity.id,
        error: String(txnError),
      });
    }

    return lossRecord;
  }

  /**
   * Look up the document number assigned to a loss record's generated documents.
   * Returns the first non-empty document_number (or entity_identifier fallback).
   */
  private async getDocumentNumberForLossRecord(lossRecordId: string): Promise<string | undefined> {
    try {
      const docParams = new URLSearchParams({
        entity_type: 'eq.document',
        is_current: 'eq.true',
        'entity_data->>related_entity_id': `eq.${lossRecordId}`,
        select: 'id,entity_identifier,entity_data',
        order: 'created_at.asc',
        limit: '10',
      });
      const docs = await httpClient.get<
        {
          id: string;
          entity_identifier: string | null;
          entity_data: Record<string, unknown> | null;
        }[]
      >(`/api/rest/data/v1/entities?${docParams}`);

      for (const doc of docs) {
        const docNum = doc.entity_data?.document_number;
        if (typeof docNum === 'string' && docNum.length > 0) {
          return docNum;
        }
      }
      for (const doc of docs) {
        if (typeof doc.entity_identifier === 'string' && doc.entity_identifier.length > 0) {
          return doc.entity_identifier;
        }
      }
    } catch {
      // Best-effort — fall through to undefined
    }
    return undefined;
  }

  /**
   * Complete a loss record
   * For DA7923: also removes assets from hand receipt
   */
  async complete(input: CompleteLossRecordInput): Promise<LossRecord> {
    const existing = await this.get(input.id);
    if (!existing) {
      throw new Error(`Loss record not found: ${input.id}`);
    }

    if (existing.entity_data.workflow_status === 'complete') {
      throw new Error('Record is already complete');
    }

    const now = new Date().toISOString();
    const updatedData: LossRecordData = {
      ...existing.entity_data,
      workflow_status: 'complete',
      completed_at: now,
      uploaded_document_ids: [
        ...(existing.entity_data.uploaded_document_ids ?? []),
        ...input.uploadedDocumentIds,
      ],
    };

    const entity = await entityService.update({
      type: 'loss_record',
      id: input.id,
      patch: {
        entity_data: updatedData as unknown as Record<string, unknown>,
        display_name: `${existing.entity_data.transaction_type} - complete`,
      },
    });

    // For DA7923, remove assets from hand receipt on Complete
    // (DD200 removes on create, so skip here)
    // Pass the loss record's document number so the HR removal transaction
    // shares the same number instead of generating a new one (AB#29441).
    if (existing.entity_data.transaction_type === 'DA7923') {
      const sourceDocumentNumber = await this.getDocumentNumberForLossRecord(existing.id);
      await this.removeAssetsFromHandReceipts(
        existing.entity_data.person_id,
        existing.entity_data.asset_ids,
        sourceDocumentNumber,
        existing.id,
      );
    }

    // Create a transaction entity so this appears in the Transaction Log
    try {
      await this.createTransactionLogEntry(existing, now, input.performedById);
    } catch (txnError) {
      log.warn('Failed to create transaction log entry for loss record', {
        id: input.id,
        error: String(txnError),
      });
    }

    return {
      id: entity.id,
      entity_identifier: entity.entity_identifier ?? null,
      display_name: entity.display_name,
      entity_data: entity.entity_data as unknown as LossRecordData,
      status: entity.status,
      created_at: entity.created_at,
      updated_at: entity.updated_at,
      created_by: entity.created_by ?? null,
    };
  }

  /**
   * Create a transaction entity for the Transaction Log when a loss record is completed.
   */
  private async createTransactionLogEntry(
    lossRecord: LossRecord,
    completedAt: string,
    performedById: string,
  ): Promise<void> {
    const { transaction_type, person_id, asset_snapshots } = lossRecord.entity_data;

    // Fetch person display name
    let memberName = '';
    try {
      const personParams = new URLSearchParams({
        entity_type: 'eq.person',
        is_current: 'eq.true',
        id: `eq.${person_id}`,
        select: 'id,display_name',
      });
      const persons = await httpClient.get<{ id: string; display_name: string | null }[]>(
        `/api/rest/data/v1/entities?${personParams}`,
      );
      memberName = persons[0]?.display_name ?? '';
    } catch {
      // Best-effort — transaction still logs without the name
    }

    // Look up related documents for the document number and for linking
    let documentNumber = '';
    let documentId = '';
    const allDocs: { id: string; entity_data: Record<string, unknown> | null }[] = [];
    try {
      const docParams = new URLSearchParams({
        entity_type: 'eq.document',
        is_current: 'eq.true',
        'entity_data->>related_entity_id': `eq.${lossRecord.id}`,
        select: 'id,entity_identifier,entity_data',
        order: 'created_at.asc',
        limit: '10',
      });
      const docs = await httpClient.get<
        {
          id: string;
          entity_identifier: string | null;
          entity_data: Record<string, unknown> | null;
        }[]
      >(`/api/rest/data/v1/entities?${docParams}`);
      for (const doc of docs) {
        allDocs.push(doc);
        const docNum = doc.entity_data?.document_number;
        if (typeof docNum === 'string' && docNum.length > 0 && !documentNumber) {
          documentNumber = docNum;
          documentId = doc.id;
        }
      }
      // Fall back to entity_identifier if no document has document_number
      if (!documentNumber) {
        for (const doc of docs) {
          if (typeof doc.entity_identifier === 'string' && doc.entity_identifier.length > 0) {
            documentNumber = doc.entity_identifier;
            documentId = doc.id;
            break;
          }
        }
      }
      if (!documentId && docs[0]) {
        documentId = docs[0].id;
      }
    } catch {
      // Best-effort
    }

    // Build line_items_snapshot from asset snapshots so assets display in the log.
    // Omit null values so the payload conforms to the transaction entity schema
    // where lin/nsn/nomenclature are typed as string (not nullable).
    const lineItemsSnapshot = (asset_snapshots ?? []).map((snapshot) => {
      const item: Record<string, unknown> = { quantity: 1 };
      if (snapshot.id) item.line_item_id = snapshot.id;
      if (snapshot.lin) item.lin = snapshot.lin;
      if (snapshot.nsn) item.nsn = snapshot.nsn;
      if (snapshot.display_name) item.nomenclature = snapshot.display_name;
      return item;
    });

    const transactionIdentifier = `TX-${transaction_type}-${lossRecord.id}`;

    // Check for an existing transaction created during document generation
    // (ensureLossRecordTransaction creates TX-LOSS-{id} when a document is first
    // generated).  Re-use it so the same entry is visible from suspense through
    // completion instead of a second, duplicate row appearing.
    let existingTxn: { id: string; entity_data: Record<string, unknown> | null } | null = null;
    try {
      const txnSearchParams = new URLSearchParams({
        entity_type: 'eq.transaction',
        is_current: 'eq.true',
        'metadata->>loss_record_id': `eq.${lossRecord.id}`,
        select: 'id,entity_data',
        order: 'created_at.desc',
        limit: '1',
      });
      const txns = await httpClient.get<
        { id: string; entity_data: Record<string, unknown> | null }[]
      >(`/api/rest/data/v1/entities?${txnSearchParams}`);
      if (txns[0]?.id) {
        existingTxn = txns[0];
      }
    } catch {
      // Best-effort — fall through to create
    }

    const txnEntityData = {
      transaction_type: transaction_type,
      date_processed: completedAt,
      performed_by_id: performedById,
      member_name: memberName,
      member_id: person_id,
      document_type: transaction_type,
      document_entity_identifier: documentNumber || transactionIdentifier,
      document_display_name: `${transaction_type} Packet`,
      ...(documentId ? { document_id: documentId } : {}),
      source: 'loss-record-complete',
      ...(lineItemsSnapshot.length > 0 ? { line_items_snapshot: lineItemsSnapshot } : {}),
    } as Record<string, unknown>;

    let txnEntity: { id: string };
    if (existingTxn) {
      // Merge: preserve any fields the document-generation path set (e.g. tags)
      // while overlaying completion-time fields.
      txnEntity = await entityService.update({
        type: 'transaction',
        id: existingTxn.id,
        patch: {
          entity_data: {
            ...(existingTxn.entity_data ?? {}),
            ...txnEntityData,
          },
          display_name: `Loss Record • ${transaction_type}`,
        },
      });
    } else {
      txnEntity = await entityService.create({
        entity_type: 'transaction',
        entity_identifier: transactionIdentifier,
        display_name: `Loss Record • ${transaction_type}`,
        entity_data: txnEntityData,
        metadata: { tags: ['PBO'], loss_record_id: lossRecord.id },
        status: 'active',
        client_reference: `txn-loss-${lossRecord.id}`,
      });
    }

    // Link documents to the transaction so they appear in the transaction detail.
    // We must send the full merged entity_data because update_entity_data replaces it.
    if (allDocs.length > 0 && txnEntity.id) {
      for (const doc of allDocs) {
        try {
          await entityService.update({
            type: 'document',
            id: doc.id,
            patch: {
              entity_data: {
                ...(doc.entity_data ?? {}),
                transaction_id: txnEntity.id,
              },
            },
          });
        } catch {
          // Best-effort — continue linking other documents
        }
      }
    }

    // Also link payload entities so they appear in the Payloads section.
    // Payloads reference the loss record via nested payload.relatedEntityId or
    // top-level related_entity_id.
    try {
      const payloadParams = new URLSearchParams({
        entity_type: 'eq.document_generation_payload',
        is_current: 'eq.true',
        or: `(entity_data->>related_entity_id.eq.${lossRecord.id},entity_data->payload->>relatedEntityId.eq.${lossRecord.id})`,
        select: 'id,entity_data',
        limit: '20',
      });
      const payloadEntities = await httpClient.get<
        { id: string; entity_data: Record<string, unknown> | null }[]
      >(`/api/rest/data/v1/entities?${payloadParams}`);
      for (const pe of payloadEntities) {
        if (!pe.entity_data?.transaction_id) {
          try {
            await entityService.update({
              type: 'document_generation_payload',
              id: pe.id,
              patch: {
                entity_data: {
                  ...(pe.entity_data ?? {}),
                  transaction_id: txnEntity.id,
                },
              },
            });
          } catch {
            // Best-effort
          }
        }
      }
    } catch {
      // Best-effort — payloads may not exist yet
    }

    log.info('Transaction log entry created for loss record', {
      lossRecordId: lossRecord.id,
      transactionType: transaction_type,
      documentCount: allDocs.length,
      assetCount: lineItemsSnapshot.length,
    });
  }

  /**
   * Append a generated document ID to the loss record's generated_document_ids list.
   * Best-effort: callers should catch and log any error rather than blocking the user.
   */
  async addGeneratedDocumentId(lossRecordId: string, documentId: string): Promise<void> {
    const existing = await this.get(lossRecordId);
    if (!existing) {
      throw new Error(`Loss record not found: ${lossRecordId}`);
    }
    const updatedIds = [...(existing.entity_data.generated_document_ids ?? []), documentId];
    await entityService.update({
      type: 'loss_record',
      id: lossRecordId,
      patch: {
        entity_data: {
          ...(existing.entity_data as unknown as Record<string, unknown>),
          generated_document_ids: updatedIds,
        },
      },
    });
  }
}

export const lossRecordService = new LossRecordService();
